import { flowRight, get } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import withAuth from '../../../app/hoc/with-auth';
import withPermissions from '../../../app/hoc/with-permissions';
import withLayoutProps from '../../hoc/with-layout-props';
import withFeedPostsProps from '../../hoc/with-feed-posts-props';
import fetchFeedPosts from '../../../app/actions/fetch-feed-posts';
import { incrementPostLikeCount } from '../../../app/actions/increment-post-like-count';
import { fetchCategoryPosts } from '../../../app/actions/fetch-category-posts';
import { pageOpened } from '../../../app/actions/page-opened';
import { getPostCount } from '../../../app/selectors/post-selectors';
import { getEntityCount } from '../../../app/selectors/pagination-selectors';
import { getIsAppLoaded } from '../../../app/selectors/is-app-loaded-selectors';
import { setAppLoaded } from '../../../app/actions/app-loaded';
import { fetchWidgetPosts } from '../../services/fetch-widget-posts';
import PostList from '../post-list';
import AccountSuspended from '../../../app/components/account-suspended';
import AnimatedLoader from '../../../app/components/animated-loader';
import NoPosts from '../no-posts';
import { getWidgetIsFeaturedPost } from '../../selectors/app-settings-selectors';

const reset = () => ({ page: 1 });
const increment = (state) => ({ page: state.page + 1 });

export class WidgetPosts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 2, // ssr fetches the first one
    };
  }

  componentDidMount() {
    const { isAppLoaded, pageOpened, setAppLoaded } = this.props;

    if (isAppLoaded) {
      return;
    }

    pageOpened({ page: 'custom-feed-widget' });
    setAppLoaded();
  }

  componentWillReceiveProps = ({ categoryId, numberOfPosts, posts, isFeaturedOnly }) => {
    const numberOfPostsChanged = numberOfPosts !== this.props.numberOfPosts;
    const categoryChanged = categoryId !== this.props.categoryId;
    const featuredOnlyChanged = isFeaturedOnly !== this.props.isFeaturedOnly;
    const shouldReset =
      categoryChanged || (numberOfPostsChanged && numberOfPosts > posts.length) || featuredOnlyChanged;

    if (shouldReset) {
      this.setState(reset, () => this.fetchPosts());
    }
  };

  fetchPosts = () => {
    if (this.props.isBlocked) {
      return;
    }

    const { categoryId, fetchFeedPosts, fetchCategoryPosts, postsPerPage, isFeaturedOnly } = this.props;
    const { page } = this.state;
    const paginationParams = { page, pageSize: postsPerPage };
    const request = fetchWidgetPosts({
      categoryId,
      paginationParams,
      fetchCategoryPosts,
      fetchFeedPosts,
      featuredOnly: isFeaturedOnly,
    });
    this.setState(increment);
    return request;
  };

  handleLikeClick = (postId) => {
    this.props.incrementPostLikeCount(postId);
  };

  renderPosts() {
    const { isBlocked, posts, isLoaded, isLoading, hasMorePosts, hasPostLimit } = this.props;
    const hasPosts = Boolean(posts.length);

    if (isBlocked) {
      return <AccountSuspended />;
    }

    if (isLoaded && !hasPosts) {
      return <NoPosts />;
    }

    return (
      <PostList
        posts={posts}
        onLikeClick={this.handleLikeClick}
        hasMorePosts={hasMorePosts}
        hasPostLimit={hasPostLimit}
        loadMore={this.fetchPosts}
        isLoading={isLoading}
      />
    );
  }

  render() {
    const { isLoaded } = this.props;

    return <AnimatedLoader isLoading={!isLoaded}>{this.renderPosts()}</AnimatedLoader>;
  }
}

WidgetPosts.propTypes = {
  fetchFeedPosts: PropTypes.func.isRequired,
  fetchCategoryPosts: PropTypes.func.isRequired,
  pageOpened: PropTypes.func.isRequired,
  incrementPostLikeCount: PropTypes.func.isRequired,
  numberOfPosts: PropTypes.number.isRequired,
  setAppLoaded: PropTypes.func.isRequired,
  hasMorePosts: PropTypes.bool.isRequired,
  hasPostLimit: PropTypes.bool.isRequired,
  posts: PropTypes.array.isRequired,
  postsPerPage: PropTypes.number.isRequired,
  categoryId: PropTypes.string,
  isBlocked: PropTypes.bool,
  isLoading: PropTypes.bool,
  isLoaded: PropTypes.bool,
  isAppLoaded: PropTypes.bool.isRequired,
  isFeaturedOnly: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, { categoryId, posts: allPosts, isLoading, isLoaded, postsPerPage, hasPostLimit }) => {
  const isFeaturedOnly = getWidgetIsFeaturedPost(state);
  const filteredPosts = allPosts.filter((post) => {
    if (categoryId && !post.categoryIds.includes(categoryId)) {
      return false;
    }
    return isFeaturedOnly ? Boolean(post.isFeatured) : true;
  });
  const numberOfPosts = get(state, 'page', 1) * postsPerPage;
  const posts = hasPostLimit ? filteredPosts.slice(0, numberOfPosts) : filteredPosts;
  const underLimit = hasPostLimit ? posts.length < postsPerPage : true;
  const hasMorePosts = underLimit && getPostCount(state) < getEntityCount(state, 'posts');

  return {
    categoryId,
    posts,
    numberOfPosts,
    hasMorePosts,
    isLoading,
    isLoaded,
    isAppLoaded: getIsAppLoaded(state),
    isFeaturedOnly,
  };
};

const mapDispatchToProps = {
  fetchFeedPosts,
  fetchCategoryPosts,
  incrementPostLikeCount,
  pageOpened,
  setAppLoaded,
};

// prettier-ignore
export default flowRight(
  withPermissions,
  withLayoutProps,
  withFeedPostsProps,
  connect(mapStateToProps, mapDispatchToProps),
  withAuth
)(WidgetPosts);
